import React from 'react';
import { getStatusColor, getChartGridColor } from '@t-one-internal/design-system';

interface GoalProgressRow {
  scopeInstanceName: string;
  avgProgress: number;
  totalGoals: number;
  onTrackCount: number;
  atRiskCount: number;
  behindCount: number;
}

interface GoalProgressChartProps {
  data: Array<Record<string, unknown>>;
}

const COLORS = {
  onTrack: getStatusColor('completed'),
  atRisk: getStatusColor('pending'),
  behind: getStatusColor('cancelled'),
  empty: getChartGridColor('axis'),
};

const toGoalProgressRow = (raw: Record<string, unknown>): GoalProgressRow => ({
  scopeInstanceName: (raw.scopeInstanceName as string) ?? 'Unknown',
  avgProgress: Number(raw.avgProgress ?? 0),
  totalGoals: Number(raw.totalGoals ?? 0),
  onTrackCount: Number(raw.onTrackCount ?? 0),
  atRiskCount: Number(raw.atRiskCount ?? 0),
  behindCount: Number(raw.behindCount ?? 0),
});

export const GoalProgressChart: React.FC<GoalProgressChartProps> = ({ data }) => {
  const rows = (!data || !Array.isArray(data) || data.length === 0)
    ? []
    : data.map(toGoalProgressRow);

  if (rows.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>
        No goal progress data available
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', height: '100%', overflow: 'auto', scrollbarWidth: 'none' }}>
      {rows.map((row, index) => {
        const total = row.onTrackCount + row.atRiskCount + row.behindCount;
        const onTrackPct = total > 0 ? (row.onTrackCount / total) * 100 : 0;
        const atRiskPct = total > 0 ? (row.atRiskCount / total) * 100 : 0;
        const behindPct = total > 0 ? (row.behindCount / total) * 100 : 0;

        return (
          <div key={`${row.scopeInstanceName}-${index}`}>
            {/* Unit name + avg progress */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                {row.scopeInstanceName}
              </span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(row.avgProgress)}% avg
              </span>
            </div>

            {/* Stacked bar */}
            <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', backgroundColor: COLORS.empty }}>
              {onTrackPct > 0 && (
                <div style={{ width: `${onTrackPct}%`, backgroundColor: COLORS.onTrack, borderRadius: onTrackPct === 100 ? '5px' : '5px 0 0 5px' }} />
              )}
              {atRiskPct > 0 && (
                <div style={{ width: `${atRiskPct}%`, backgroundColor: COLORS.atRisk }} />
              )}
              {behindPct > 0 && (
                <div style={{ width: `${behindPct}%`, backgroundColor: COLORS.behind }} />
              )}
            </div>

            {/* Counts */}
            <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '11px', color: '#9CA3AF' }}>
              <span>{row.onTrackCount} on track</span>
              <span>{row.atRiskCount} at risk</span>
              <span>{row.behindCount} behind</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
